// Словарь слов экрана: код → слово (`GET /api/screen-words`).
//
// ⚠⚠ **Клиент своих слов не держит** (`G117`, таски 72 и 74). Слово, которое
// видит человек, живёт в одном словаре `data/screen-words.json`, и его отдаёт
// одна ручка. Код бывает двух видов: код случая, который отдаёт ручка сервера
// (`kind`), и код места на экране, которое печатает клиент (запись словаря с
// `owner: "client"`). Здесь — чтение словаря, память о нём и подстановка чисел.
//
// ⚠⚠ **Словарь читается до первого экрана** (`main.tsx`) и переспрашивается
// после входа и выхода: до входа сервер отдаёт только слова входа, после входа —
// весь. Экраны читают слово синхронно (`w`), а корень приложения
// перерисовывается, когда словарь сменился (`useWordsVersion`).
//
// ⚠ Слова нет вовсе (словарь не ответил или записи нет) — показывается код.
// Пустое место хуже: человек не узнает, что что-то не так, а код хотя бы можно
// назвать тому, кто чинит.

import { useSyncExternalStore } from "react";

import { ScreenWords } from "@/api/schemas";
import type { RuleRef } from "@/money/types";

export type Words = Readonly<Record<string, string | null>>;

const PATH = "/api/screen-words";

let kept: Words | null = null;
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
  version += 1;
  for (const one of listeners) one();
}

/** Положить словарь в память и перерисовать тех, кто на него подписан. */
export function setWords(words: Words): void {
  kept = words;
  changed();
}

/** Забыть словарь: следующее обращение спросит сервер заново. */
export function forgetWords(): void {
  kept = null;
  changed();
}

async function fetchWords(): Promise<Words | null> {
  try {
    const answer = await fetch(PATH, { headers: { accept: "application/json" } });
    if (!answer.ok) return null;
    const parsed = ScreenWords.safeParse(await answer.json());
    return parsed.success ? parsed.data.words : null;
  } catch {
    return null;
  }
}

/**
 * Спросить словарь у сервера и положить в память. `false` — сервер не ответил;
 * прежний словарь (если был) остаётся.
 */
export async function loadWords(): Promise<boolean> {
  const got = await fetchWords();
  if (got === null) return false;
  setWords(got);
  return true;
}

/** Словарь, в котором есть все названные коды (или переспрошенный один раз). */
export async function wordsFor(kinds: readonly string[]): Promise<Words> {
  if (kept === null || kinds.some((one) => !(one in kept!))) {
    const got = await fetchWords();
    if (got !== null) setWords(got);
  }
  return kept ?? {};
}

/** Числа и предметы, которые подставляются в слово на место `{имя}`. */
export type Slots = Readonly<Record<string, string | number>>;

/** Подставить числа в слово: `{day}` → день. Имени нет в `slots` — место остаётся. */
export function fill(word: string, slots: Slots = {}): string {
  return word.replace(/\{([a-z_]+)\}/g, (whole, name: string) =>
    name in slots ? String(slots[name]) : whole,
  );
}

/** Слово кода с подставленными числами. Слова нет — код. */
export function say(words: Words, kind: string, slots: Slots = {}): string {
  const word = words[kind];
  if (typeof word !== "string" || word.trim() === "") return kind;
  return fill(word, slots);
}

/**
 * Первая буква заглавной. Слово поля в словаре одно (`field_login` — «логін»):
 * внутри фразы отказа оно строчное, в подписи поля — с заглавной.
 */
export function capital(word: string): string {
  return word === "" ? word : word.charAt(0).toLocaleUpperCase("uk-UA") + word.slice(1);
}

/** Три формы украинского счёта: 1 справа · 2 справи · 5 справ (в слове — `{n}`). */
export interface Plural {
  readonly one: string;
  readonly few: string;
  readonly many: string;
}

const COUNTING = new Intl.PluralRules("uk");

/**
 * Форма слова по числу — одно место на все «N справ», «N розбіжностей» (`Intl.PluralRules`).
 * Число подставляется в `{n}` готовой строкой; «other» (дроби) читается как «many».
 */
export function plural(forms: Plural, n: number, shown: string = String(n)): string {
  const rule = COUNTING.select(n);
  return fill(rule === "one" || rule === "few" ? forms[rule] : forms.many, { n: shown });
}

/** Слово кода из словаря в памяти — синхронно, для разметки. */
export function w(kind: string, slots: Slots = {}): string {
  return say(kept ?? {}, kind, slots);
}

/** Есть ли слово у кода в словаре в памяти. До входа, например, названий поверхностей нет. */
export function known(kind: string): boolean {
  const word = kept?.[kind];
  return typeof word === "string" && word.trim() !== "";
}

/** Слово одного кода — спросить словарь и сказать. */
export async function wordOf(kind: string, slots: Slots = {}): Promise<string> {
  return say(await wordsFor([kind]), kind, slots);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Номер словаря: меняется, когда словарь сменился, — повод перерисовать экран. */
export function useWordsVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => version);
}

// --- Коды мест на экране -------------------------------------------------------
//
// ⚠ Каталог слов поверхности (`surfaces/S-NN/uk.ts`) — **не слова, а коды**: он
// перечисляет, какой код печатается в каком месте, а слово к коду берётся из
// словаря в момент показа. Каждый код, объявленный так, записывается в перечень
// (`clientCodes`), и тест `client/tests/words-in-dictionary.test.ts` сверяет
// перечень со словарём в обе стороны: ни кода без слова, ни слова без места.

/** Код места и то, нужен ли он до входа (признак `before_login` записи). */
const CODES = new Map<string, boolean>();

/** Коды мест, объявленные каталогами клиента: код → нужен ли до входа. */
export function clientCodes(): ReadonlyMap<string, boolean> {
  return CODES;
}

type Spec = string | readonly Spec[] | { readonly [key: string]: Spec };

/** Каталог кодов → каталог слов той же формы. Поле `rule` — код правила, не слово. */
export type Said<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly Said<U>[]
    : { readonly [K in keyof T]: K extends "rule" ? T[K] : Said<T[K]> };

function remember(code: string, beforeLogin: boolean): void {
  CODES.set(code, (CODES.get(code) ?? false) || beforeLogin);
}

function build(spec: Spec, beforeLogin: boolean): unknown {
  if (typeof spec === "string") {
    remember(spec, beforeLogin);
    return spec;
  }
  if (Array.isArray(spec)) {
    return spec.map((one: Spec) => wrap(one, beforeLogin));
  }
  return wrap(spec, beforeLogin);
}

function wrap(spec: Spec, beforeLogin: boolean): unknown {
  if (typeof spec === "string") {
    remember(spec, beforeLogin);
    return w(spec);
  }
  if (Array.isArray(spec)) return build(spec, beforeLogin);
  const out: Record<string, unknown> = {};
  for (const [key, one] of Object.entries(spec as Record<string, Spec>)) {
    if (key === "rule") {
      out[key] = one;
      continue;
    }
    if (typeof one === "string") {
      remember(one, beforeLogin);
      Object.defineProperty(out, key, { enumerable: true, get: () => w(one) });
    } else {
      Object.defineProperty(out, key, { enumerable: true, value: wrap(one, beforeLogin) });
    }
  }
  return out;
}

/**
 * Каталог слов по каталогу кодов: та же форма, на месте кода — слово словаря в
 * момент чтения. `beforeLogin` — слова нужны до входа (экран входа, каркас).
 *
 * ⚠ Строки массива берутся словом в момент сборки массива: массив читают
 * целиком, при показе, поэтому массив собирается заново на каждое чтение поля.
 */
export function fromDictionary<const T extends { readonly [key: string]: Spec }>(
  spec: T,
  { beforeLogin = false }: { readonly beforeLogin?: boolean } = {},
): Said<T> {
  const out: Record<string, unknown> = {};
  for (const [key, one] of Object.entries(spec as Record<string, Spec>)) {
    if (key === "rule") {
      out[key] = one;
      continue;
    }
    if (typeof one === "string") {
      remember(one, beforeLogin);
      Object.defineProperty(out, key, { enumerable: true, get: () => w(one) });
    } else if (Array.isArray(one)) {
      build(one, beforeLogin);
      Object.defineProperty(out, key, {
        enumerable: true,
        get: () => (one as readonly Spec[]).map((item) => wrap(item, beforeLogin)),
      });
    } else {
      Object.defineProperty(out, key, {
        enumerable: true,
        value: fromDictionary(one as { readonly [key: string]: Spec }, { beforeLogin }),
      });
    }
  }
  return out as Said<T>;
}

/**
 * Коды подсказки «?» одного элемента: что это · как читать · сноска (механика).
 * `G117`: у каждого элемента своя подсказка, механика расчёта — сноской.
 */
export function hint<const P extends string, const R extends RuleRef>(prefix: P, rule: R) {
  return {
    what: `${prefix}_hint_what`,
    read: `${prefix}_hint_read`,
    note: `${prefix}_hint_note`,
    rule,
  } as const;
}

/**
 * Коды подсказки поля ввода: то же, что `hint`, плюс «приклад» и «якщо
 * порожньо» (F-087). Пример — он же текст в пустом поле.
 */
export function fieldHint<const P extends string, const R extends RuleRef>(prefix: P, rule: R) {
  return {
    ...hint(prefix, rule),
    example: `${prefix}_hint_example`,
    empty: `${prefix}_hint_empty`,
  } as const;
}
